import {
  Carriers,
  carrierToLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo } from "react";

const CARRIER_OPTIONS = [
  { value: Carriers.UPS, label: carrierToLabel[Carriers.UPS] },
  { value: Carriers.FEDEX, label: carrierToLabel[Carriers.FEDEX] },
];

export const ThirdPartyBillingInput = groupInput({
  accountName: input<string>({ validator: nonEmptyValidator }),
  carrier: input<Carriers>({ validator: nonEmptyValidator }),
  accountNumber: input<string>({ validator: nonEmptyValidator }),
  country: input<string>({ validator: nonEmptyValidator }),
  postalCode: input<string>({ validator: nonEmptyValidator }),
});

export type ThirdPartyBillingValue = InputSchema.Value<
  typeof ThirdPartyBillingInput
>;

export type ThirdPartyBillingForm = InputSchema.Input<
  typeof ThirdPartyBillingInput
>;

export const thirdPartyBillingDefault = (): ThirdPartyBillingValue => ({
  accountName: "",
  carrier: Carriers.UPS,
  accountNumber: "",
  country: "",
  postalCode: "",
});

export const CreateThirdPartyBillingForm = memo(
  function CreateThirdPartyBillingForm({
    form,
  }: {
    form: ThirdPartyBillingForm;
  }) {
    return (
      <Flex dir="column" gap="lg">
        <RedoTextInput
          label="Account name"
          placeholder="Enter an identifier for this account"
          setValue={form.inputs.accountName.setValue}
          value={form.inputs.accountName.value}
        />

        <RedoSingleSelectDropdownInput
          label="Carrier"
          options={CARRIER_OPTIONS.map((carrier) => ({
            id: carrier.value,
            type: "text",
            text: carrier.label,
            value: carrier.value,
          }))}
          optionSelected={({ item }) => {
            form.inputs.carrier.setValue(item.value as Carriers);
          }}
          placeholder="Select a carrier"
          selectedItem={{
            id: form.inputs.carrier.value,
            type: "text",
            text: carrierToLabel[form.inputs.carrier.value],
            value: form.inputs.carrier.value,
          }}
        />
        {form.inputs.carrier.value === Carriers.UPS && (
          <Text color="tertiary" fontSize="xxs" fontWeight="regular">
            UPS® does not support Third Party Billing for UPS Ground Saver®
            rates. These rates will not be available for purchase with Third
            Party Billing.{" "}
            <a
              href="https://www.ups.com/assets/resources/webcontent/en_US/SurePost_Terms.pdf"
              rel="noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
              target="_blank"
            >
              Terms of Service
            </a>
          </Text>
        )}

        <RedoTextInput
          label="Account number"
          placeholder="Enter account number"
          setValue={form.inputs.accountNumber.setValue}
          value={form.inputs.accountNumber.value}
        />

        <RedoTextInput
          label="Country code"
          maxLength={2}
          placeholder="Enter country code"
          setValue={form.inputs.country.setValue}
          value={form.inputs.country.value}
        />

        <RedoTextInput
          label="Postal code"
          placeholder="Enter postal code"
          setValue={form.inputs.postalCode.setValue}
          value={form.inputs.postalCode.value}
        />
      </Flex>
    );
  },
);
